/** A point on the arena's ground plane, in arena metres. */
export interface GroundPoint {
  x: number;
  z: number;
}

/**
 * What a device wants this step, in the world's terms rather than the fighter's own.
 *
 * Under the fixed camera (D-16) a player pushes a direction on the screen and points at a place,
 * neither of which depends on which way the fighter happens to be facing. Turning that into the
 * fighter-local `FighterInput` the simulation takes needs the fighter's own yaw, so the arena
 * stage does it — see `intentToInput`.
 */
export interface FighterIntent {
  /** Where to go, in world space on the ground plane; magnitude 0…1. */
  moveX: number;
  moveZ: number;
  /**
   * Where to face. A thumbstick names a direction (`aimDir`), which the fighter faces at once; a
   * cursor names a place (`aimAt`), which an aimed attack turns it to. At most one is ever set, and
   * with neither in play the fighter faces the way it moves (`facingOf` in the arena).
   */
  aimAt: GroundPoint | null;
  aimDir: GroundPoint | null;
  dash: boolean;
  attack: boolean;
  /** Horizontal bend requested for a spellcaster's projectile, −1 … 1. */
  steer?: number;
  /** The player gives the duel up from the device itself (a pad's Start held down). */
  forfeit?: boolean;
}

export interface FighterInputMode {
  /** This fighter is a Wizard/Sorceress-style projectile spellcaster. */
  steerableProjectile: boolean;
  /** One of that fighter's projectiles is currently in flight. */
  projectileInFlight: boolean;
}

/** Projects a screen position onto the arena's ground plane; null when it misses the plane. */
export type GroundProjector = (clientX: number, clientY: number) => GroundPoint | null;

/** A device (mouse and keyboard, VR controllers) turned into an intent. */
export interface FighterInputSource {
  /** Polls the device; call once per rendered frame before stepping the simulation. */
  update(dtSeconds: number): void;
  /** Intent for one simulation step. A press between two steps counts even if already released. */
  next(mode?: FighterInputMode): FighterIntent;
  /** How far a hold that gives the duel up has come, while one is held; a device without one omits it. */
  forfeitHold?(): ForfeitHold | null;
  /**
   * Whether the device is there: `false` while a seat's own controller is missing, so the panel
   * can say so. Sources that cannot go away (the keyboard) leave it out.
   */
  connected?(): boolean;
  /**
   * A short buzz in the player's hands, `strength` 0 … 1 for `seconds`, where the device has a
   * motor (a pad's rumble, a Quest controller's): a blow taken or landed is felt as well as seen.
   */
  pulse?(strength: number, seconds: number): void;
  dispose(): void;
}

/** A button held down to give the duel up, part of the way there. */
export interface ForfeitHold {
  /** 0 … 1 of `FORFEIT_HOLD_SECONDS`. */
  share: number;
  /** What is being held, for the prompt: "B or Y", "Start". */
  button: string;
}

/** A hold this long gives the duel up; a tap does nothing, so a knock cannot. */
export const FORFEIT_HOLD_SECONDS = 1.5;

/** Gives the duel up once, when a button has been held for `FORFEIT_HOLD_SECONDS`; letting go rearms it. */
export class ForfeitLatch {
  private heldSeconds = 0;
  private pending = false;

  constructor(private readonly button: string) {}

  set(down: boolean, dtSeconds: number): void {
    const before = this.heldSeconds;
    this.heldSeconds = down ? this.heldSeconds + Math.max(0, dtSeconds) : 0;
    if (before < FORFEIT_HOLD_SECONDS && this.heldSeconds >= FORFEIT_HOLD_SECONDS)
      this.pending = true;
  }

  /** True once, on the step after the hold reached its length. */
  take(): boolean {
    const fired = this.pending;
    this.pending = false;
    return fired;
  }

  progress(): ForfeitHold | null {
    return this.heldSeconds > 0 && this.heldSeconds < FORFEIT_HOLD_SECONDS
      ? { share: this.heldSeconds / FORFEIT_HOLD_SECONDS, button: this.button }
      : null;
  }

  reset(): void {
    this.heldSeconds = 0;
    this.pending = false;
  }
}

export const NO_INTENT: FighterIntent = {
  moveX: 0,
  moveZ: 0,
  aimAt: null,
  aimDir: null,
  dash: false,
  attack: false,
  steer: 0,
};

/** Press-and-release detection between simulation steps. */
export class Latch {
  private held = false;
  private pressed = false;

  set(down: boolean): void {
    if (down && !this.held) this.pressed = true;
    this.held = down;
  }

  /** True when held now or pressed since the last call. */
  take(): boolean {
    const value = this.held || this.pressed;
    this.pressed = false;
    return value;
  }

  reset(): void {
    this.held = false;
    this.pressed = false;
  }
}
